import React, { useState, useEffect, useCallback } from 'react'; // Add useEffect and useCallback
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { apiService } from '../services/apiService';
import { notificationService } from '../services/notificationService';
import { Document } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import ConfirmDialog from './ConfirmDialog';

interface DocumentsProps {
  // No props needed as it will fetch its own data
}

const Documents: React.FC<DocumentsProps> = () => { // Remove documents, onDocumentsChange from props
  const { effectiveTheme } = useTheme();
  const navigate = useNavigate(); // Initialize useNavigate
  const [documents, setDocuments] = useState<Document[]>([]); // Add local documents state
  const [isLoading, setIsLoading] = useState(true); // Set initial loading to true
  const [selectedDocuments, setSelectedDocuments] = useState<Set<number>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [confirmProps, setConfirmProps] = useState({
    title: '',
    message: '',
    confirmText: '',
    type: 'primary' as 'primary' | 'danger' | 'secondary' | 'success' | 'warning' | 'info' | 'light' | 'dark',
    onConfirm: () => {},
  });

  const getFileIcon = (fileType: string) => {
    switch(fileType.toLowerCase()) {
      case 'pdf': return 'pdf';
      case 'docx': return 'word';
      case 'pptx': return 'ppt';
      case 'xlsx': return 'excel';
      default: return 'text';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'completed': return 'check-circle';
      case 'processing': return 'gear';
      case 'failed': return 'x-circle';
      case 'uploaded': return 'upload';
      default: return 'circle';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch(status) {
      case 'completed': return 'status-completed';
      case 'processing': return 'status-processing';
      case 'failed': return 'status-failed';
      default: return 'status-processing';
    }
  };

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatSummaryAsHtml = (summary: string) => {
    if (!summary) return '';

    let html = summary
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
      .replace(/\*(.*?)\*/g, '<em>$1</em>'); // Italic

    // Handle list items (lines starting with - )
    html = html.split('\n').map(line => {
      if (line.trim().startsWith('- ')) {
        return `<li>${line.trim().substring(2).trim()}</li>`;
      }
      return line;
    }).join('');

    // Wrap list items in ul if they exist
    if (html.includes('<li>')) {
      html = `<ul>${html}</ul>`;
    }

    // Wrap remaining lines in paragraphs
    html = html.split('\n').map(line => {
      if (line.trim() !== '' && !line.trim().startsWith('<ul') && !line.trim().startsWith('<li')) {
        return `<p>${line.trim()}</p>`;
      }
      return line;
    }).join('');

    return html;
  };

  const loadDocuments = useCallback(async () => { // Define loadDocuments locally
    try {
      setIsLoading(true);
      const docs = await apiService.get('/documents/');
      setDocuments(docs);
    } catch (error) {
      console.error('Error loading documents:', error);
      notificationService.show('Failed to load documents', 'error');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { // useEffect to load documents on mount
    loadDocuments();
  }, [loadDocuments]);

  const handleRefresh = async () => {
    setIsLoading(true);
    notificationService.info('🔄 Refreshing documents...', 2000);
    await loadDocuments(); // Call local loadDocuments
    setIsLoading(false);
  };

  const handleViewDocument = async (documentId: number) => {
    try {
      const doc = await apiService.getDocument(documentId);
      showDocumentModal(doc);
    } catch (error) {
      console.error('Error loading document details:', error);
      notificationService.error('Failed to load document details');
    }
  };

  const handleQueryWithAI = (documentId: number, projectId: number) => {
    console.log('Closing document modal and navigating to chat with document ID:', documentId, 'and Project ID:', projectId);
    // Hide the Bootstrap modal first
    const documentModalElement = document.getElementById('documentModal');
    if (documentModalElement) {
      const { Modal } = require('bootstrap');
      const modalInstance = Modal.getInstance(documentModalElement);
      if (modalInstance) {
        modalInstance.hide();
      }
    }
    // Navigate to the chat page with the document ID and Project ID as query parameters
    navigate(`/chat?project_id=${projectId}&doc_id=${documentId}`);
  };

  const showDocumentModal = (doc: Document) => {
    const progress = Math.round(doc.processing_progress * 100);
    const uploadDate = new Date(doc.upload_timestamp).toLocaleString();
    
    // Create modal HTML (similar to original implementation)
    const modalHtml = `
      <div class="modal fade" id="documentModal" tabindex="-1">
        <div class="modal-dialog modal-lg">
          <div class="modal-content ${effectiveTheme === 'dark' ? 'dark-mode-modal-content' : ''}" style="border-radius: var(--radius-2xl); border: none; box-shadow: var(--shadow-xl);">
            <div class="modal-header ${effectiveTheme === 'dark' ? 'dark-mode-modal-header' : ''}" style="background: var(--primary-gradient); color: white; border-radius: var(--radius-2xl) var(--radius-2xl) 0 0;">
              <h5 class="modal-title">
                <i class="bi bi-file-earmark-${getFileIcon(doc.file_type)} me-2"></i>
                ${doc.original_filename}
              </h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
            </div>
            <div class="modal-body p-4 ${effectiveTheme === 'dark' ? 'dark-mode-modal-body' : ''}">
              <div class="row g-4">
                <div class="col-md-6">
                  <h6 class="fw-bold text-primary mb-3 ${effectiveTheme === 'dark' ? 'text-white' : ''}">
                    <i class="bi bi-info-circle me-2"></i>
                    Document Information
                  </h6>
                  <div class="mb-2 ${effectiveTheme === 'dark' ? 'text-light' : ''}">
                    <strong>Type:</strong>
                    <span class="badge bg-secondary ms-1">${doc.document_type || 'Unknown'}</span>
                  </div>
                  <div class="mb-2 ${effectiveTheme === 'dark' ? 'text-light' : ''}">
                    <strong>Pages:</strong> ${doc.page_count || 'N/A'}
                  </div>
                  <div class="mb-2 ${effectiveTheme === 'dark' ? 'text-light' : ''}">
                    <strong>Words:</strong> ${doc.word_count ? doc.word_count.toLocaleString() : 'N/A'}
                  </div>
                  <div class="mb-2 ${effectiveTheme === 'dark' ? 'text-light' : ''}">
                    <strong>Status:</strong>
                    <span class="status-badge ${getStatusBadgeClass(doc.status)} ms-1">
                      <i class="bi bi-${getStatusIcon(doc.status)} me-1"></i>
                      ${doc.status}
                    </span>
                  </div>
                </div>
                <div class="col-md-6">
                  <h6 class="fw-bold text-primary mb-3 ${effectiveTheme === 'dark' ? 'text-white' : ''}">
                    <i class="bi bi-gear me-2"></i>
                    Processing Details
                  </h6>
                  <div class="mb-2 ${effectiveTheme === 'dark' ? 'text-light' : ''}">
                    <strong>Upload Date:</strong> ${uploadDate}
                  </div>
                  <div class="mb-2 ${effectiveTheme === 'dark' ? 'text-light' : ''}">
                    <strong>File Size:</strong> ${formatFileSize(doc.file_size)}
                  </div>
                  <div class="mb-3 ${effectiveTheme === 'dark' ? 'text-light' : ''}">
                    <strong>Progress:</strong>
                    <div class="progress mt-1" style="height: 8px;">
                      <div class="progress-bar" style="width: ${progress}%"></div>
                    </div>
                    <small class="text-muted">${progress}% complete</small>
                  </div>
                </div>
              </div>
              ${doc.summary ? `
                <div class="mt-4">
                  <h6 class="fw-bold text-primary mb-3 ${effectiveTheme === 'dark' ? 'text-white' : ''}">
                    <i class="bi bi-file-text me-2"></i>
                    Summary
                  </h6>
                  <div class="p-3 rounded ${effectiveTheme === 'dark' ? 'dark-mode-summary-bg' : ''}" style="background: rgba(102, 126, 234, 0.05); border-left: 4px solid var(--primary-color); max-height: 200px; overflow-y: auto;">
                    <div class="mb-0 ${effectiveTheme === 'dark' ? 'text-light' : ''}">${formatSummaryAsHtml(doc.summary)}</div>
                  </div>
                </div>
              ` : ''}
            </div>
            <div class="modal-footer ${effectiveTheme === 'dark' ? 'dark-mode-modal-footer' : ''}">
              <button type="button" class="btn btn-secondary ${effectiveTheme === 'dark' ? 'btn-dark-mode-secondary' : ''}" data-bs-dismiss="modal">
                <i class="bi bi-x me-1"></i>
                Close
              </button>
              ${doc.status === 'completed' ? `
                <button type="button" class="btn btn-primary ${effectiveTheme === 'dark' ? 'btn-dark-mode-primary' : ''}" id="queryWithAIButton-${doc.id}">
                  <i class="bi bi-chat-dots me-1"></i>
                  Query with AI
                </button>
              ` : ''}
            </div>
          </div>
        </div>
      </div>
    `;
    
    // Remove existing modal
    const existingModal = document.getElementById('documentModal');
    if (existingModal) {
      existingModal.remove();
    }
    
    // Add new modal
    document.body.insertAdjacentHTML('beforeend', modalHtml);
    
    // Show modal with Bootstrap
    const { Modal } = require('bootstrap');
    const modal = new Modal(document.getElementById('documentModal'));
    modal.show();

    // Attach event listener to the "Query with AI" button
    const queryButton = document.getElementById(`queryWithAIButton-${doc.id}`);
    if (queryButton) {
      queryButton.addEventListener('click', () => handleQueryWithAI(doc.id, doc.project_id || 0));
    }
  };

  const handleDeleteDocument = (documentId: number) => {
    setConfirmProps({
      title: 'Delete Document',
      message: 'Are you sure you want to delete this document? This action cannot be undone.',
      confirmText: 'Delete',
      type: 'danger',
      onConfirm: async () => {
        setIsConfirmingDelete(true);
        try {
          await apiService.deleteDocument(documentId);
          notificationService.success('✅ Document deleted successfully');
          loadDocuments(); // Call local loadDocuments
        } catch (error) {
          console.error('Error deleting document:', error);
          notificationService.error('❌ Failed to delete document');
        } finally {
          setIsConfirmingDelete(false);
          setShowConfirm(false);
        }
      },
    });
    setShowConfirm(true);
  };

  const handleCopyToProject = async (documentId: number, documentName: string) => {
    try {
      // Get user's projects
      const response = await apiService.getProjects();
      const projects = Array.isArray(response) ? response : (response?.projects || []);
      
      if (projects.length === 0) {
        notificationService.info('No projects available to copy to');
        return;
      }

      // Show project selection modal
      const selectedProjectId = await showProjectSelectionModal(projects, documentName);
      
      if (!selectedProjectId) return;

      // Copy document to selected project
      await apiService.copyDocumentToProject(documentId, { project_id: selectedProjectId });
      notificationService.success('✅ Document copied to project successfully');
      
    } catch (error) {
      console.error('Error copying document to project:', error);
      notificationService.error('❌ Failed to copy document to project');
    }
  };

  const handleBulkCopyToProject = async () => {
    if (selectedDocuments.size === 0) {
      notificationService.warning('Please select documents to copy');
      return;
    }

    try {
      // Get user's projects
      const response = await apiService.getProjects();
      const projects = Array.isArray(response) ? response : (response?.projects || []);
      
      if (projects.length === 0) {
        notificationService.info('No projects available to copy to');
        return;
      }

      // Show project selection modal
      const selectedProjectId = await showProjectSelectionModal(projects, `${selectedDocuments.size} documents`);
      
      if (!selectedProjectId) return;

      // Copy all selected documents
      const promises = Array.from(selectedDocuments).map(async (documentId) => {
        return apiService.copyDocumentToProject(documentId, { project_id: selectedProjectId });
      });

      await Promise.all(promises);
      notificationService.success(`✅ ${selectedDocuments.size} documents copied to project successfully`);
      setSelectedDocuments(new Set());
      setShowBulkActions(false);
      
    } catch (error) {
      console.error('Error copying documents to project:', error);
      notificationService.error('❌ Failed to copy documents to project');
    }
  };

  const handleSelectDocument = (documentId: number, isSelected: boolean) => {
    const newSelected = new Set(selectedDocuments);
    if (isSelected) {
      newSelected.add(documentId);
    } else {
      newSelected.delete(documentId);
    }
    setSelectedDocuments(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allDocIds = new Set(documents.map(doc => doc.id));
      setSelectedDocuments(allDocIds);
      setShowBulkActions(true);
    } else {
      setSelectedDocuments(new Set());
      setShowBulkActions(false);
    }
  };

  const showProjectSelectionModal = (projects: any[], documentName: string): Promise<number | null> => {
    return new Promise((resolve) => {
      const modalHtml = `
        <div class="modal fade" id="projectSelectionModal" tabindex="-1">
          <div class="modal-dialog modal-dialog-centered">
            <div
              class="modal-content"
              style="
                border-radius: var(--radius-lg);
                border: 1px solid var(--border-color);
                box-shadow: var(--shadow-md);
                background-color: var(--bg-color);
                color: var(--text-primary);
              "
            >
              <div
                class="modal-header"
                style="
                  background: var(--surface-color);
                  color: var(--text-primary);
                  border-bottom: 1px solid var(--border-color);
                  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
                "
              >
                <h5 class="modal-title">
                  <i class="bi bi-folder-plus me-2"></i>
                  Copy "${documentName}" to Project
                </h5>
                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
              </div>
              <div class="modal-body">
                <p class="mb-3" style="color: var(--text-secondary);">Select a project to copy this document to:</p>
                <div class="list-group">
                  ${projects.map(project => `
                    <button
                      type="button"
                      class="list-group-item list-group-item-action project-option"
                      data-project-id="${project.id}"
                      style="
                        background-color: var(--bg-color);
                        color: var(--text-primary);
                        border: 1px solid var(--border-color);
                        margin-bottom: 0.5rem;
                        border-radius: var(--radius-md);
                        transition: all 0.2s ease;
                      "
                    >
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 class="mb-1" style="color: var(--text-primary);">${project.name}</h6>
                          <small style="color: var(--text-secondary);">${project.description || 'No description'}</small>
                        </div>
                        <div class="text-end">
                          <small style="color: var(--text-secondary);">${project.file_count || 0} files</small>
                        </div>
                      </div>
                    </button>
                  `).join('')}
                </div>
              </div>
              <div
                class="modal-footer"
                style="
                  border-top: 1px solid var(--border-color);
                  background-color: var(--surface-color);
                  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
                "
              >
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                <button type="button" class="btn btn-primary" id="copyToProjectBtn" disabled>
                  <i class="bi bi-copy me-1"></i>
                  Copy to Project
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
      
      const existingModal = document.getElementById('projectSelectionModal');
      if (existingModal) {
        existingModal.remove();
      }
      
      document.body.insertAdjacentHTML('beforeend', modalHtml);
      
      const { Modal } = require('bootstrap');
      const modal = new Modal(document.getElementById('projectSelectionModal'));
      const copyBtn = document.getElementById('copyToProjectBtn');
      let selectedProjectId: number | null = null;
      
      // Handle project selection
      document.querySelectorAll('.project-option').forEach(option => {
        option.addEventListener('click', (e) => {
          // Remove previous selection
          document.querySelectorAll('.project-option').forEach(opt => opt.classList.remove('active'));
          
          // Add selection to clicked option
          (e.currentTarget as HTMLElement).classList.add('active');
          selectedProjectId = parseInt((e.currentTarget as HTMLElement).dataset.projectId || '0');
          
          // Enable copy button
          if (copyBtn) (copyBtn as HTMLButtonElement).disabled = false;
        });
      });
      
      copyBtn?.addEventListener('click', () => {
        modal.hide();
        resolve(selectedProjectId);
      });
      
      document.getElementById('projectSelectionModal')?.addEventListener('hidden.bs.modal', () => {
        if (!selectedProjectId) resolve(null);
        document.getElementById('projectSelectionModal')?.remove();
      });
      
      modal.show();
    });
  };


  if (isLoading) {
    return (
      <div style={{
        padding: '1.5rem',
        height: 'calc(100vh - 120px)',
        minHeight: '600px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          background: 'var(--bg-color)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-color)',
          boxShadow: 'var(--shadow-sm)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem',
            borderBottom: '1px solid var(--border-color)',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Skeleton height={25} width="150px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
            <Skeleton height={35} width="100px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
          </div>
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: 'var(--surface-color)' }}>
                  {Array.from({ length: 7 }).map((_, index) => (
                    <th key={index} style={{
                      padding: '1rem',
                      borderBottom: '1px solid var(--border-color)',
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      textAlign: 'left',
                      position: 'sticky',
                      top: 0,
                      background: 'var(--surface-color)',
                      zIndex: 1
                    }}>
                      <Skeleton height={15} width={index === 0 ? '20px' : '80%'} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                      <Skeleton height={16} width={16} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    </td>
                    <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Skeleton circle height={24} width={24} style={{ marginRight: '1rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                        <div>
                          <Skeleton height={15} width="180px" style={{ marginBottom: '0.25rem' }} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                          <Skeleton height={10} width="100px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                      <Skeleton height={20} width="60px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    </td>
                    <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                      <Skeleton height={20} width="80px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    </td>
                    <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                      <Skeleton height={8} width="80px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    </td>
                    <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                      <Skeleton height={15} width="100px" baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                    </td>
                    <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <Skeleton height={30} width={30} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                        <Skeleton height={30} width={30} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                        <Skeleton height={30} width={30} baseColor={effectiveTheme === 'dark' ? '#333' : '#eee'} highlightColor={effectiveTheme === 'dark' ? '#555' : '#ddd'} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{
        padding: '1.5rem',
        height: 'calc(100vh - 120px)',
        minHeight: '600px',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{
          background: 'var(--bg-color)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-color)',
          boxShadow: 'var(--shadow-sm)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1.5rem',
            borderBottom: '1px solid var(--border-color)',
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h6 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600 }}>Documents</h6>
              {showBulkActions && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                    {selectedDocuments.size} selected
                  </span>
                  <button
                    style={{
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid var(--warning-color)',
                      background: 'var(--bg-color)',
                      color: 'var(--warning-color)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      transition: 'all var(--transition-normal)'
                    }}
                    onClick={handleBulkCopyToProject}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--warning-color)';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--bg-color)';
                      e.currentTarget.style.color = 'var(--warning-color)';
                    }}
                  >
                    <i className="bi bi-folder-plus me-1"></i>
                    Move to Project
                  </button>
                  <button
                    style={{
                      padding: '0.375rem 0.75rem',
                      fontSize: '0.875rem',
                      border: '1px solid var(--text-secondary)',
                      background: 'var(--bg-color)',
                      color: 'var(--text-secondary)',
                      borderRadius: 'var(--radius-sm)',
                      cursor: 'pointer',
                      transition: 'all var(--transition-normal)'
                    }}
                    onClick={() => {
                      setSelectedDocuments(new Set());
                      setShowBulkActions(false);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--text-secondary)';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--bg-color)';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
            <button
              style={{
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                border: '1px solid var(--primary-color)',
                background: 'var(--bg-color)',
                color: 'var(--primary-color)',
                borderRadius: 'var(--radius-sm)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all var(--transition-normal)',
                opacity: isLoading ? 0.6 : 1
              }}
              onClick={handleRefresh}
              disabled={isLoading}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.background = 'var(--primary-color)';
                  e.currentTarget.style.color = 'white';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.background = 'var(--bg-color)';
                  e.currentTarget.style.color = 'var(--primary-color)';
                }
              }}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </button>
          </div>
          <div style={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0
          }}>
            <div style={{ height: '100%' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                  color: 'var(--text-primary)'
                }}>
                  <thead>
                    <tr style={{ background: 'var(--surface-color)' }}>
                      <th style={{
                        padding: '1rem',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        textAlign: 'left',
                        position: 'sticky',
                        top: 0,
                        background: 'var(--surface-color)',
                        zIndex: 1,
                        width: '50px'
                      }}>
                        <input
                          type="checkbox"
                          style={{
                            width: '16px',
                            height: '16px',
                            accentColor: 'var(--primary-color)'
                          }}
                          checked={documents.length > 0 && selectedDocuments.size === documents.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </th>
                      <th style={{
                        padding: '1rem',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        textAlign: 'left',
                        position: 'sticky',
                        top: 0,
                        background: 'var(--surface-color)',
                        zIndex: 1
                      }}>Document</th>
                      <th style={{
                        padding: '1rem',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        textAlign: 'left',
                        position: 'sticky',
                        top: 0,
                        background: 'var(--surface-color)',
                        zIndex: 1
                      }}>Type</th>
                      <th style={{
                        padding: '1rem',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        textAlign: 'left',
                        position: 'sticky',
                        top: 0,
                        background: 'var(--surface-color)',
                        zIndex: 1
                      }}>Status</th>
                      <th style={{
                        padding: '1rem',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        textAlign: 'left',
                        position: 'sticky',
                        top: 0,
                        background: 'var(--surface-color)',
                        zIndex: 1
                      }}>Progress</th>
                      <th style={{
                        padding: '1rem',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        textAlign: 'left',
                        position: 'sticky',
                        top: 0,
                        background: 'var(--surface-color)',
                        zIndex: 1
                      }}>Date</th>
                      <th style={{
                        padding: '1rem',
                        borderBottom: '1px solid var(--border-color)',
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        textAlign: 'left',
                        position: 'sticky',
                        top: 0,
                        background: 'var(--surface-color)',
                        zIndex: 1
                      }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.length === 0 ? (
                      <tr>
                        <td colSpan={7} style={{
                          textAlign: 'center',
                          padding: '3rem 2rem',
                          borderBottom: 'none'
                        }}>
                          <i className="bi bi-inbox" style={{ fontSize: '2rem', color: 'var(--text-muted)' }}></i>
                          <p style={{ color: 'var(--text-secondary)', marginTop: '1rem', marginBottom: '0.5rem', fontWeight: 500 }}>No documents found</p>
                          <small style={{ color: 'var(--text-muted)' }}>Upload your first document to get started</small>
                        </td>
                      </tr>
                    ) : (
                      documents.map((doc, index) => {
                        const uploadDate = new Date(doc.upload_timestamp).toLocaleDateString();
                        const progress = Math.round(doc.processing_progress * 100);
                        const timeAgo = getTimeAgo(doc.upload_timestamp);
                        
                        return (
                          <tr key={doc.id} style={{
                            animation: `fadeIn 0.3s ease ${index * 0.05}s both`,
                            borderBottom: '1px solid var(--border-color)',
                            transition: 'background-color var(--transition-normal)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--surface-color)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                              <input
                                type="checkbox"
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  accentColor: 'var(--primary-color)'
                                }}
                                checked={selectedDocuments.has(doc.id)}
                                onChange={(e) => handleSelectDocument(doc.id, e.target.checked)}
                              />
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: '1rem' }}>
                                  <i
                                    className={`bi bi-file-earmark-${getFileIcon(doc.file_type)}`}
                                    style={{ fontSize: '1.5rem', color: 'var(--primary-color)' }}
                                  ></i>
                                </div>
                                <div>
                                  <div
                                    style={{
                                      fontWeight: 500,
                                      maxWidth: '200px',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}
                                    title={doc.original_filename}
                                  >
                                    {doc.original_filename}
                                  </div>
                                  {doc.title && <small style={{ color: 'var(--text-secondary)' }}>{doc.title}</small>}
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                              <span style={{
                                padding: '0.25rem 0.75rem',
                                borderRadius: 'var(--radius-sm)',
                                fontSize: '12px',
                                fontWeight: 500,
                                textTransform: 'uppercase',
                                letterSpacing: '0.025em',
                                background: 'var(--text-secondary)',
                                color: 'white'
                              }}>{doc.file_type.toUpperCase()}</span>
                              {doc.document_type && (
                                <>
                                  <br />
                                  <small style={{ color: 'var(--text-secondary)' }}>{doc.document_type}</small>
                                </>
                              )}
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                              <span className={`status-badge ${getStatusBadgeClass(doc.status)}`}>
                                <i className={`bi bi-${getStatusIcon(doc.status)} me-1`}></i>
                                {doc.status}
                              </span>
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{
                                  width: '80px',
                                  height: '8px',
                                  background: 'var(--border-color)',
                                  borderRadius: '4px',
                                  marginRight: '0.5rem',
                                  overflow: 'hidden'
                                }}>
                                  <div
                                    style={{
                                      width: `${progress}%`,
                                      height: '100%',
                                      background: 'var(--primary-color)',
                                      borderRadius: '4px',
                                      transition: 'width 0.3s ease'
                                    }}
                                  ></div>
                                </div>
                                <small style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{progress}%</small>
                              </div>
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                              <div>
                                <div style={{ fontWeight: 500 }}>{uploadDate}</div>
                                <small style={{ color: 'var(--text-secondary)' }}>{timeAgo}</small>
                              </div>
                            </td>
                            <td style={{ padding: '1rem', verticalAlign: 'middle' }}>
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                <button
                                  style={{
                                    padding: '0.375rem 0.5rem',
                                    fontSize: '0.875rem',
                                    border: '1px solid var(--primary-color)',
                                    background: 'var(--bg-color)',
                                    color: 'var(--primary-color)',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-normal)'
                                  }}
                                  onClick={() => handleViewDocument(doc.id)}
                                  title="View Details"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'var(--primary-color)';
                                    e.currentTarget.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'var(--bg-color)';
                                    e.currentTarget.style.color = 'var(--primary-color)';
                                  }}
                                >
                                  <i className="bi bi-eye"></i>
                                </button>
                                {doc.status === 'completed' && (
                                  <button
                                    style={{
                                      padding: '0.375rem 0.5rem',
                                      fontSize: '0.875rem',
                                      border: '1px solid var(--success-color)',
                                      background: 'var(--bg-color)',
                                      color: 'var(--success-color)',
                                      borderRadius: 'var(--radius-sm)',
                                      cursor: 'pointer',
                                      transition: 'all var(--transition-normal)'
                                    }}
                                    onClick={() => handleQueryWithAI(doc.id, doc.project_id || 0)}
                                    title="Query with AI"
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = 'var(--success-color)';
                                      e.currentTarget.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'var(--bg-color)';
                                      e.currentTarget.style.color = 'var(--success-color)';
                                    }}
                                  >
                                    <i className="bi bi-chat-dots"></i>
                                  </button>
                                )}
                                <button
                                  style={{
                                    padding: '0.375rem 0.5rem',
                                    fontSize: '0.875rem',
                                    border: '1px solid var(--warning-color)',
                                    background: 'var(--bg-color)',
                                    color: 'var(--warning-color)',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-normal)'
                                  }}
                                  onClick={() => handleCopyToProject(doc.id, doc.original_filename)}
                                  title="Copy to Project"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'var(--warning-color)';
                                    e.currentTarget.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'var(--bg-color)';
                                    e.currentTarget.style.color = 'var(--warning-color)';
                                  }}
                                >
                                  <i className="bi bi-folder-plus"></i>
                                </button>
                                <button
                                  style={{
                                    padding: '0.375rem 0.5rem',
                                    fontSize: '0.875rem',
                                    border: '1px solid var(--danger-color)',
                                    background: 'var(--bg-color)',
                                    color: 'var(--danger-color)',
                                    borderRadius: 'var(--radius-sm)',
                                    cursor: 'pointer',
                                    transition: 'all var(--transition-normal)'
                                  }}
                                  onClick={() => handleDeleteDocument(doc.id)}
                                  title="Delete"
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'var(--danger-color)';
                                    e.currentTarget.style.color = 'white';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'var(--bg-color)';
                                    e.currentTarget.style.color = 'var(--danger-color)';
                                  }}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <ConfirmDialog
          show={showConfirm}
          title={confirmProps.title}
          message={confirmProps.message}
          confirmText={confirmProps.confirmText}
          type={confirmProps.type}
          isConfirming={isConfirmingDelete}
          onConfirm={confirmProps.onConfirm}
          onCancel={() => {
            setShowConfirm(false);
            setIsConfirmingDelete(false);
          }}
        />
    </>
  );
};

export default Documents;
